import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { UsersIcon, DollarSignIcon, BoxIcon, GavelIcon, ClockIcon, CheckCircleIcon, XCircleIcon, AlertTriangleIcon, ArrowLeftIcon, ChevronRightIcon, UserPlusIcon, CreditCardIcon, BarChart2Icon, LayoutGridIcon, RefreshCwIcon, PackageIcon, CalendarIcon, ShieldIcon, BellIcon, CopyIcon, ShareIcon, MessageSquareIcon, FileTextIcon, LineChartIcon, PieChartIcon } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import RechartsLineChart from '../components/charts/RechartsLineChart';
import RechartsPieChart from '../components/charts/RechartsPieChart';
interface Member {
  id: string;
  name: string;
  avatar: string;
  role: string;
  contribution: number;
  contributionPercentage: number;
  joinDate: string;
  status: 'active' | 'pending';
}
interface Syndicate {
  id: string;
  name: string;
  description: string;
  members: Member[];
  totalCapital: number;
  targetCapital: number;
  status: 'open' | 'closed' | 'active' | 'completed';
  type: 'auction' | 'bulk' | 'liquidation';
  createdAt: string;
  endDate: string;
  creator: string;
  allocation: {
    type: string;
    percentage: number;
    value: number;
  }[];
  recentActivity: {
    action: string;
    user: string;
    time: string;
  }[];
  inventoryDistribution?: {
    grade: string;
    percentage: number;
    quantity: number;
  }[];
  targetAuction?: {
    id: string;
    title: string;
    devices: number;
    currentBid: string;
    timeLeft: string;
  };
}
const SyndicateDetail: React.FC = () => {
  const navigate = useNavigate();
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const [activeTab, setActiveTab] = useState('overview');
  // Mock data for demonstration
  const syndicateData: Syndicate = {
    id: id || 'syn-2023-001',
    name: 'Premium iPhone Collective',
    description: 'Group buy for carrier-grade iPhone 13 Pro and 12 Pro Max devices with focus on A/B grade inventory.',
    members: [{
      id: 'usr-001',
      name: 'Alex Johnson',
      avatar: 'https://github.com/shadcn.png',
      role: 'Creator',
      contribution: 25000,
      contributionPercentage: 25,
      joinDate: '2023-07-01',
      status: 'active'
    }, {
      id: 'usr-002',
      name: 'Sarah Miller',
      avatar: 'https://github.com/shadcn.png',
      role: 'Member',
      contribution: 15000,
      contributionPercentage: 15,
      joinDate: '2023-07-02',
      status: 'active'
    }, {
      id: 'usr-003',
      name: 'James Wilson',
      avatar: 'https://github.com/shadcn.png',
      role: 'Member',
      contribution: 20000,
      contributionPercentage: 20,
      joinDate: '2023-07-03',
      status: 'active'
    }, {
      id: 'usr-004',
      name: 'Maria Garcia',
      avatar: 'https://github.com/shadcn.png',
      role: 'Member',
      contribution: 30000,
      contributionPercentage: 30,
      joinDate: '2023-07-04',
      status: 'active'
    }, {
      id: 'usr-005',
      name: 'Robert Chen',
      avatar: 'https://github.com/shadcn.png',
      role: 'Member',
      contribution: 10000,
      contributionPercentage: 10,
      joinDate: '2023-07-05',
      status: 'pending'
    }],
    totalCapital: 90000,
    targetCapital: 100000,
    status: 'open',
    type: 'auction',
    createdAt: '2023-07-01',
    endDate: '2023-07-25',
    creator: 'Alex Johnson',
    allocation: [{
      type: 'iPhone 13 Pro (256GB)',
      percentage: 40,
      value: 40000
    }, {
      type: 'iPhone 13 Pro (128GB)',
      percentage: 30,
      value: 30000
    }, {
      type: 'iPhone 12 Pro Max (256GB)',
      percentage: 20,
      value: 20000
    }, {
      type: 'iPhone 12 Pro Max (128GB)',
      percentage: 10,
      value: 10000
    }],
    recentActivity: [{
      action: 'Increased contribution by $5,000',
      user: 'Maria Garcia',
      time: '2 hours ago'
    }, {
      action: 'Joined the syndicate',
      user: 'Robert Chen',
      time: '1 day ago'
    }, {
      action: 'Updated target allocation',
      user: 'Alex Johnson',
      time: '2 days ago'
    }],
    targetAuction: {
      id: 'AUC-2023-1245',
      title: 'Bulk iPhone 12 & 13 Models',
      devices: 25,
      currentBid: '$10,250',
      timeLeft: '12h 32m'
    }
  };
  const contributionData = [{
    date: 'Jul 01',
    value: 25000
  }, {
    date: 'Jul 02',
    value: 40000
  }, {
    date: 'Jul 03',
    value: 60000
  }, {
    date: 'Jul 04',
    value: 90000
  }, {
    date: 'Jul 05',
    value: 90000
  }];
  const memberDistributionData = [{
    label: 'Alex Johnson',
    value: 25,
    color: '#3b82f6'
  }, {
    label: 'Maria Garcia',
    value: 30,
    color: '#10b981'
  }, {
    label: 'James Wilson',
    value: 20,
    color: '#f59e0b'
  }, {
    label: 'Sarah Miller',
    value: 15,
    color: '#8b5cf6'
  }, {
    label: 'Robert Chen',
    value: 10,
    color: '#ec4899'
  }];
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'outline';
      case 'active':
        return 'success';
      case 'closed':
        return 'secondary';
      case 'completed':
        return 'secondary';
      default:
        return 'default';
    }
  };
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // In a real app, you would show a toast notification here
  };
  return <div className="space-y-6" data-id="element-2492">
      <div className="flex items-center justify-between" data-id="element-2493">
        <div className="flex items-center" data-id="element-2494">
          <Button variant="ghost" size="sm" className="mr-2" onClick={() => navigate('/syndicate-hub')} data-id="element-2495">
            <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-2496" />
            Back to Hub
          </Button>
          <h1 className="text-2xl font-bold" data-id="element-2497">{syndicateData.name}</h1>
          <Badge variant={getStatusColor(syndicateData.status)} className="ml-2" data-id="element-2498">
            {syndicateData.status.charAt(0).toUpperCase() + syndicateData.status.slice(1)}
          </Badge>
          <Badge variant="outline" className="ml-2 capitalize" data-id="element-2499">
            {syndicateData.type}
          </Badge>
        </div>
        <div className="flex items-center space-x-2" data-id="element-2500">
          <TooltipProvider data-id="element-2501">
            <Tooltip data-id="element-2502">
              <TooltipTrigger asChild data-id="element-2503">
                <Button variant="outline" size="icon" onClick={() => copyToClipboard(syndicateData.id)} data-id="element-2504">
                  <CopyIcon className="h-4 w-4" data-id="element-2505" />
                </Button>
              </TooltipTrigger>
              <TooltipContent data-id="element-2506">
                <p data-id="element-2507">Copy Syndicate ID</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider data-id="element-2508">
            <Tooltip data-id="element-2509">
              <TooltipTrigger asChild data-id="element-2510">
                <Button variant="outline" size="icon" data-id="element-2511">
                  <ShareIcon className="h-4 w-4" data-id="element-2512" />
                </Button>
              </TooltipTrigger>
              <TooltipContent data-id="element-2513">
                <p data-id="element-2514">Share Syndicate</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider data-id="element-2515">
            <Tooltip data-id="element-2516">
              <TooltipTrigger asChild data-id="element-2517">
                <Button variant="outline" size="icon" data-id="element-2518">
                  <BellIcon className="h-4 w-4" data-id="element-2519" />
                </Button>
              </TooltipTrigger>
              <TooltipContent data-id="element-2520">
                <p data-id="element-2521">Notifications</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Button onClick={() => navigate(`/syndicate-contribution/${id}`)} data-id="element-2522">
            <DollarSignIcon className="mr-2 h-4 w-4" data-id="element-2523" />
            Contribute
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-2524">
        <Card data-id="element-2525">
          <CardContent className="p-4" data-id="element-2526">
            <div className="flex items-center justify-between" data-id="element-2527">
              <div data-id="element-2528">
                <p className="text-sm text-muted-foreground" data-id="element-2529">Capital Raised</p>
                <p className="text-2xl font-bold" data-id="element-2530">
                  {formatCurrency(syndicateData.totalCapital)}
                </p>
              </div>
              <DollarSignIcon className="h-8 w-8 text-muted-foreground/30" data-id="element-2531" />
            </div>
            <Progress value={syndicateData.totalCapital / syndicateData.targetCapital * 100} className="h-1 mt-2" data-id="element-2532" />
            <p className="text-xs text-muted-foreground mt-1" data-id="element-2533">
              {(syndicateData.totalCapital / syndicateData.targetCapital * 100).toFixed(0)}
              % of {formatCurrency(syndicateData.targetCapital)} target
            </p>
          </CardContent>
        </Card>
        <Card data-id="element-2534">
          <CardContent className="p-4" data-id="element-2535">
            <div className="flex items-center justify-between" data-id="element-2536">
              <div data-id="element-2537">
                <p className="text-sm text-muted-foreground" data-id="element-2538">Members</p>
                <p className="text-2xl font-bold" data-id="element-2539">
                  {syndicateData.members.length}
                </p>
              </div>
              <UsersIcon className="h-8 w-8 text-muted-foreground/30" data-id="element-2540" />
            </div>
            <div className="flex -space-x-2 mt-2" data-id="element-2541">
              {syndicateData.members.slice(0, 5).map((member, i) => <Avatar key={i} className="h-6 w-6 border-2 border-background" data-id="element-2542">
                  <AvatarImage src={member.avatar} data-id="element-2543" />
                  <AvatarFallback data-id="element-2544">{member.name.charAt(0)}</AvatarFallback>
                </Avatar>)}
              {syndicateData.members.length > 5 && <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center text-xs border-2 border-background" data-id="element-2545">
                  +{syndicateData.members.length - 5}
                </div>}
            </div>
          </CardContent>
        </Card>
        <Card data-id="element-2546">
          <CardContent className="p-4" data-id="element-2547">
            <div className="flex items-center justify-between" data-id="element-2548">
              <div data-id="element-2549">
                <p className="text-sm text-muted-foreground" data-id="element-2550">Time Remaining</p>
                <p className="text-2xl font-bold" data-id="element-2551">12 days</p>
              </div>
              <ClockIcon className="h-8 w-8 text-muted-foreground/30" data-id="element-2552" />
            </div>
            <p className="text-xs text-muted-foreground mt-2" data-id="element-2553">
              End Date: {syndicateData.endDate}
            </p>
          </CardContent>
        </Card>
        <Card data-id="element-2554">
          <CardContent className="p-4" data-id="element-2555">
            <div className="flex items-center justify-between" data-id="element-2556">
              <div data-id="element-2557">
                <p className="text-sm text-muted-foreground" data-id="element-2558">Your Share</p>
                <p className="text-2xl font-bold" data-id="element-2559">25%</p>
              </div>
              <PieChartIcon className="h-8 w-8 text-muted-foreground/30" data-id="element-2560" />
            </div>
            <p className="text-xs text-muted-foreground mt-2" data-id="element-2561">
              Your Contribution: {formatCurrency(25000)}
            </p>
          </CardContent>
        </Card>
      </div>
      <Tabs defaultValue="overview" value={activeTab} onValueChange={setActiveTab} data-id="element-2562">
        <TabsList className="mb-4" data-id="element-2563">
          <TabsTrigger value="overview" data-id="element-2564">Overview</TabsTrigger>
          <TabsTrigger value="members" data-id="element-2565">Members</TabsTrigger>
          <TabsTrigger value="allocation" data-id="element-2566">Allocation</TabsTrigger>
          <TabsTrigger value="activity" data-id="element-2567">Activity</TabsTrigger>
          <TabsTrigger value="documents" data-id="element-2568">Documents</TabsTrigger>
          <TabsTrigger value="analytics" data-id="element-2569">Analytics</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="space-y-6" data-id="element-2570">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2571">
            <div className="md:col-span-2 space-y-6" data-id="element-2572">
              <Card data-id="element-2573">
                <CardHeader data-id="element-2574">
                  <CardTitle data-id="element-2575">Syndicate Overview</CardTitle>
                  <CardDescription data-id="element-2576">{syndicateData.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4" data-id="element-2577">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-2578">
                    <div className="space-y-1 mb-2 md:mb-0" data-id="element-2579">
                      <p className="text-sm text-muted-foreground" data-id="element-2580">
                        Created By
                      </p>
                      <div className="flex items-center" data-id="element-2581">
                        <Avatar className="h-6 w-6 mr-2" data-id="element-2582">
                          <AvatarImage src="https://github.com/shadcn.png" data-id="element-2583" />
                          <AvatarFallback data-id="element-2584">AJ</AvatarFallback>
                        </Avatar>
                        <span className="font-medium" data-id="element-2585">
                          {syndicateData.creator}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-1 mb-2 md:mb-0" data-id="element-2586">
                      <p className="text-sm text-muted-foreground" data-id="element-2587">
                        Created On
                      </p>
                      <div className="flex items-center" data-id="element-2588">
                        <CalendarIcon className="h-4 w-4 mr-2 text-muted-foreground" data-id="element-2589" />
                        <span data-id="element-2590">{syndicateData.createdAt}</span>
                      </div>
                    </div>
                    <div className="space-y-1" data-id="element-2591">
                      <p className="text-sm text-muted-foreground" data-id="element-2592">
                        Syndicate ID
                      </p>
                      <div className="flex items-center" data-id="element-2593">
                        <span className="font-mono text-sm" data-id="element-2594">
                          {syndicateData.id}
                        </span>
                        <Button variant="ghost" size="icon" className="h-6 w-6 ml-1" onClick={() => copyToClipboard(syndicateData.id)} data-id="element-2595">
                          <CopyIcon className="h-3 w-3" data-id="element-2596" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="pt-4 border-t" data-id="element-2597">
                    <div className="space-y-1 mb-4" data-id="element-2598">
                      <p className="text-sm text-muted-foreground" data-id="element-2599">
                        Capital Raised
                      </p>
                      <div className="flex items-end" data-id="element-2600">
                        <span className="text-2xl font-bold" data-id="element-2601">
                          {formatCurrency(syndicateData.totalCapital)}
                        </span>
                        <span className="text-sm text-muted-foreground ml-2" data-id="element-2602">
                          of {formatCurrency(syndicateData.targetCapital)}
                        </span>
                      </div>
                    </div>
                    <Progress value={syndicateData.totalCapital / syndicateData.targetCapital * 100} className="h-2" data-id="element-2603" />
                    <div className="flex justify-between mt-2 text-xs text-muted-foreground" data-id="element-2604">
                      <span data-id="element-2605">0%</span>
                      <span data-id="element-2606">
                        Goal: {formatCurrency(syndicateData.targetCapital)}
                      </span>
                      <span data-id="element-2607">100%</span>
                    </div>
                  </div>
                  {syndicateData.type === 'auction' && syndicateData.targetAuction && <div className="mt-6 p-4 border rounded-md bg-muted/30" data-id="element-2608">
                        <h3 className="text-sm font-medium mb-2" data-id="element-2609">
                          Target Auction
                        </h3>
                        <div className="flex justify-between items-center" data-id="element-2610">
                          <div data-id="element-2611">
                            <p className="font-medium" data-id="element-2612">
                              {syndicateData.targetAuction.title}
                            </p>
                            <p className="text-sm text-muted-foreground" data-id="element-2613">
                              ID: {syndicateData.targetAuction.id}
                            </p>
                          </div>
                          <div className="text-right" data-id="element-2614">
                            <p className="font-medium" data-id="element-2615">
                              {syndicateData.targetAuction.currentBid}
                            </p>
                            <div className="flex items-center text-sm text-muted-foreground" data-id="element-2616">
                              <ClockIcon className="h-3 w-3 mr-1" data-id="element-2617" />
                              <span data-id="element-2618">
                                {syndicateData.targetAuction.timeLeft}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-end mt-2" data-id="element-2619">
                          <Button variant="outline" size="sm" onClick={() => navigate('/wholesale-auctions')} data-id="element-2620">
                            View Auction
                          </Button>
                        </div>
                      </div>}
                </CardContent>
              </Card>
              <Card data-id="element-2621">
                <CardHeader data-id="element-2622">
                  <CardTitle data-id="element-2623">Capital Growth</CardTitle>
                  <CardDescription data-id="element-2624">
                    Total capital raised over time
                  </CardDescription>
                </CardHeader>
                <CardContent data-id="element-2625">
                  <RechartsLineChart data={contributionData} height={250} color="#3b82f6" gradient={true} yAxisFormatter={value => `$${value}`} tooltipFormatter={value => [`$${value}`, 'Capital Raised']} data-id="element-2626" />
                </CardContent>
              </Card>
            </div>
            <div className="space-y-6" data-id="element-2627">
              <Card data-id="element-2628">
                <CardHeader data-id="element-2629">
                  <CardTitle data-id="element-2630">Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4" data-id="element-2631">
                  {syndicateData.status === 'open' && <>
                      <Button className="w-full" onClick={() => navigate(`/syndicate-contribution/${id}`)} data-id="element-2632">
                        <DollarSignIcon className="mr-2 h-4 w-4" data-id="element-2633" />
                        Contribute Funds
                      </Button>
                      <Button variant="outline" className="w-full" data-id="element-2634">
                        <UserPlusIcon className="mr-2 h-4 w-4" data-id="element-2635" />
                        Invite Members
                      </Button>
                      {syndicateData.type === 'auction' && <Button variant="outline" className="w-full" data-id="element-2636">
                          <GavelIcon className="mr-2 h-4 w-4" data-id="element-2637" />
                          View Target Auction
                        </Button>}
                    </>}
                  {syndicateData.status === 'active' && <>
                      <Button className="w-full" data-id="element-2638">
                        <LayoutGridIcon className="mr-2 h-4 w-4" data-id="element-2639" />
                        Manage Inventory
                      </Button>
                      <Button variant="outline" className="w-full" data-id="element-2640">
                        <BarChart2Icon className="mr-2 h-4 w-4" data-id="element-2641" />
                        View Performance
                      </Button>
                      <Button variant="outline" className="w-full" data-id="element-2642">
                        <div className="mr-2 h-4 w-4" data-id="element-2643" />
                        Trade Allocation
                      </Button>
                    </>}
                  {syndicateData.status === 'completed' && <>
                      <Button className="w-full" data-id="element-2644">
                        <BarChart2Icon className="mr-2 h-4 w-4" data-id="element-2645" />
                        View Final Report
                      </Button>
                      <Button variant="outline" className="w-full" data-id="element-2646">
                        <RefreshCwIcon className="mr-2 h-4 w-4" data-id="element-2647" />
                        Start New Syndicate
                      </Button>
                    </>}
                </CardContent>
              </Card>
              <Card data-id="element-2648">
                <CardHeader data-id="element-2649">
                  <CardTitle data-id="element-2650">Recent Activity</CardTitle>
                </CardHeader>
                <CardContent data-id="element-2651">
                  <div className="space-y-4" data-id="element-2652">
                    {syndicateData.recentActivity.map((activity, index) => <div key={index} className="flex items-start" data-id="element-2653">
                        <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center mr-3 mt-0.5" data-id="element-2654">
                          {index === 0 ? <DollarSignIcon className="h-4 w-4" data-id="element-2655" /> : index === 1 ? <UserPlusIcon className="h-4 w-4" data-id="element-2656" /> : <RefreshCwIcon className="h-4 w-4" data-id="element-2657" />}
                        </div>
                        <div data-id="element-2658">
                          <p className="font-medium" data-id="element-2659">{activity.action}</p>
                          <div className="flex items-center text-sm text-muted-foreground" data-id="element-2660">
                            <span data-id="element-2661">{activity.user}</span>
                            <span className="mx-1" data-id="element-2662">•</span>
                            <span data-id="element-2663">{activity.time}</span>
                          </div>
                        </div>
                      </div>)}
                  </div>
                </CardContent>
              </Card>
              <Card data-id="element-2664">
                <CardHeader data-id="element-2665">
                  <CardTitle data-id="element-2666">Financial Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4" data-id="element-2667">
                  <div className="flex justify-between items-center pb-2 border-b" data-id="element-2668">
                    <span className="text-sm text-muted-foreground" data-id="element-2669">
                      Your Contribution
                    </span>
                    <span className="font-medium" data-id="element-2670">
                      {formatCurrency(syndicateData.members.find(m => m.name === 'Alex Johnson')?.contribution || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pb-2 border-b" data-id="element-2671">
                    <span className="text-sm text-muted-foreground" data-id="element-2672">
                      Your Share
                    </span>
                    <span className="font-medium" data-id="element-2673">
                      {syndicateData.members.find(m => m.name === 'Alex Johnson')?.contributionPercentage}
                      %
                    </span>
                  </div>
                  <div className="flex justify-between items-center pb-2 border-b" data-id="element-2674">
                    <span className="text-sm text-muted-foreground" data-id="element-2675">
                      Platform Fee
                    </span>
                    <span className="font-medium" data-id="element-2676">2.5%</span>
                  </div>
                  <div className="flex justify-between items-center pb-2 border-b" data-id="element-2677">
                    <span className="text-sm text-muted-foreground" data-id="element-2678">
                      Escrow Fee
                    </span>
                    <span className="font-medium" data-id="element-2679">1.0%</span>
                  </div>
                  {syndicateData.status === 'completed' && <div className="flex justify-between items-center pt-2" data-id="element-2680">
                      <span className="text-sm font-medium" data-id="element-2681">ROI</span>
                      <span className="font-medium text-green-600" data-id="element-2682">+15.8%</span>
                    </div>}
                </CardContent>
              </Card>
              <Card data-id="element-2683">
                <CardHeader data-id="element-2684">
                  <CardTitle data-id="element-2685">Need Help?</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4" data-id="element-2686">
                  <Button variant="outline" className="w-full justify-start" data-id="element-2687">
                    <MessageSquareIcon className="mr-2 h-4 w-4" data-id="element-2688" />
                    Contact Syndicate Creator
                  </Button>
                  <Button variant="outline" className="w-full justify-start" data-id="element-2689">
                    <FileTextIcon className="mr-2 h-4 w-4" data-id="element-2690" />
                    View Syndicate Rules
                  </Button>
                  <Button variant="outline" className="w-full justify-start" data-id="element-2691">
                    <ShieldIcon className="mr-2 h-4 w-4" data-id="element-2692" />
                    Escrow Protection Details
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="members" className="space-y-6" data-id="element-2693">
          <Card data-id="element-2694">
            <CardHeader data-id="element-2695">
              <div className="flex items-center justify-between" data-id="element-2696">
                <div data-id="element-2697">
                  <CardTitle data-id="element-2698">Syndicate Members</CardTitle>
                  <CardDescription data-id="element-2699">
                    All participants in this syndicate
                  </CardDescription>
                </div>
                <div className="flex items-center space-x-2" data-id="element-2700">
                  <Button variant="outline" size="sm" data-id="element-2701">
                    <UserPlusIcon className="mr-2 h-4 w-4" data-id="element-2702" />
                    Invite Members
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent data-id="element-2703">
              <div className="overflow-x-auto" data-id="element-2704">
                <table className="w-full" data-id="element-2705">
                  <thead data-id="element-2706">
                    <tr className="border-b" data-id="element-2707">
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground" data-id="element-2708">
                        Member
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground" data-id="element-2709">
                        Role
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground" data-id="element-2710">
                        Contribution
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground" data-id="element-2711">
                        Share
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground" data-id="element-2712">
                        Join Date
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground" data-id="element-2713">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-2714">
                    {syndicateData.members.map(member => <tr key={member.id} className="border-b hover:bg-muted/50" data-id="element-2715">
                        <td className="py-3 px-4" data-id="element-2716">
                          <div className="flex items-center gap-2" data-id="element-2717">
                            <Avatar className="h-8 w-8" data-id="element-2718">
                              <AvatarImage src={member.avatar} data-id="element-2719" />
                              <AvatarFallback data-id="element-2720">
                                {member.name.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="font-medium" data-id="element-2721">{member.name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4" data-id="element-2722">{member.role}</td>
                        <td className="py-3 px-4" data-id="element-2723">
                          {formatCurrency(member.contribution)}
                        </td>
                        <td className="py-3 px-4" data-id="element-2724">
                          {member.contributionPercentage}%
                        </td>
                        <td className="py-3 px-4" data-id="element-2725">{member.joinDate}</td>
                        <td className="py-3 px-4" data-id="element-2726">
                          {member.status === 'active' ? <Badge variant="success" data-id="element-2727">Active</Badge> : <Badge variant="warning" data-id="element-2728">Pending</Badge>}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2729">
            <Card data-id="element-2730">
              <CardHeader data-id="element-2731">
                <CardTitle data-id="element-2732">Contribution Distribution</CardTitle>
                <CardDescription data-id="element-2733">
                  Breakdown of syndicate shares by member
                </CardDescription>
              </CardHeader>
              <CardContent data-id="element-2734">
                <div className="flex justify-center" data-id="element-2735">
                  <RechartsPieChart data={memberDistributionData} height={250} donut={true} innerRadius={60} outerRadius={100} tooltipFormatter={(value, name) => [`${value}%`, name]} data-id="element-2736" />
                </div>
              </CardContent>
            </Card>
            <Card data-id="element-2737">
              <CardHeader data-id="element-2738">
                <CardTitle data-id="element-2739">Member Activity</CardTitle>
                <CardDescription data-id="element-2740">Recent member actions</CardDescription>
              </CardHeader>
              <CardContent data-id="element-2741">
                <div className="space-y-4" data-id="element-2742">
                  <div className="flex items-start p-3 border rounded-md" data-id="element-2743">
                    <Avatar className="h-8 w-8 mr-3 mt-0.5" data-id="element-2744">
                      <AvatarImage src="https://github.com/shadcn.png" data-id="element-2745" />
                      <AvatarFallback data-id="element-2746">MG</AvatarFallback>
                    </Avatar>
                    <div data-id="element-2747">
                      <p className="font-medium" data-id="element-2748">
                        Maria Garcia increased contribution by $5,000
                      </p>
                      <p className="text-xs text-muted-foreground mt-1" data-id="element-2749">
                        2 hours ago
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start p-3 border rounded-md" data-id="element-2750">
                    <Avatar className="h-8 w-8 mr-3 mt-0.5" data-id="element-2751">
                      <AvatarImage src="https://github.com/shadcn.png" data-id="element-2752" />
                      <AvatarFallback data-id="element-2753">RC</AvatarFallback>
                    </Avatar>
                    <div data-id="element-2754">
                      <p className="font-medium" data-id="element-2755">
                        Robert Chen joined the syndicate
                      </p>
                      <p className="text-xs text-muted-foreground mt-1" data-id="element-2756">
                        1 day ago
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start p-3 border rounded-md" data-id="element-2757">
                    <Avatar className="h-8 w-8 mr-3 mt-0.5" data-id="element-2758">
                      <AvatarImage src="https://github.com/shadcn.png" data-id="element-2759" />
                      <AvatarFallback data-id="element-2760">AJ</AvatarFallback>
                    </Avatar>
                    <div data-id="element-2761">
                      <p className="font-medium" data-id="element-2762">
                        Alex Johnson updated target allocation
                      </p>
                      <p className="text-xs text-muted-foreground mt-1" data-id="element-2763">
                        2 days ago
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        <TabsContent value="allocation" className="space-y-6" data-id="element-2764">
          <Card data-id="element-2765">
            <CardHeader data-id="element-2766">
              <CardTitle data-id="element-2767">Allocation Plan</CardTitle>
              <CardDescription data-id="element-2768">
                How the syndicate capital will be distributed
              </CardDescription>
            </CardHeader>
            <CardContent data-id="element-2769">
              <div className="overflow-x-auto" data-id="element-2770">
                <table className="w-full" data-id="element-2771">
                  <thead data-id="element-2772">
                    <tr className="border-b" data-id="element-2773">
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground" data-id="element-2774">
                        Device Type
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground" data-id="element-2775">
                        Allocation
                      </th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground" data-id="element-2776">
                        Value
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-2777">
                    {syndicateData.allocation.map((item, index) => <tr key={index} className="border-b" data-id="element-2778">
                        <td className="py-3 px-4 font-medium" data-id="element-2779">{item.type}</td>
                        <td className="py-3 px-4" data-id="element-2780">{item.percentage}%</td>
                        <td className="py-3 px-4 text-right" data-id="element-2781">
                          {formatCurrency(item.value)}
                        </td>
                      </tr>)}
                    <tr className="bg-muted/30" data-id="element-2782">
                      <td className="py-3 px-4 font-medium" data-id="element-2783">Total</td>
                      <td className="py-3 px-4 font-medium" data-id="element-2784">
                        {syndicateData.allocation.reduce((acc, curr) => acc + curr.percentage, 0)}
                        %
                      </td>
                      <td className="py-3 px-4 text-right font-medium" data-id="element-2785">
                        {formatCurrency(syndicateData.allocation.reduce((acc, curr) => acc + curr.value, 0))}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2786">
            <Card data-id="element-2787">
              <CardHeader data-id="element-2788">
                <CardTitle data-id="element-2789">Allocation Visualization</CardTitle>
                <CardDescription data-id="element-2790">
                  Breakdown of allocation by device type
                </CardDescription>
              </CardHeader>
              <CardContent data-id="element-2791">
                <div className="space-y-4" data-id="element-2792">
                  {syndicateData.allocation.map((item, index) => <div key={index} className="flex items-center" data-id="element-2793">
                      <div className="w-32 text-sm truncate" data-id="element-2794">{item.type}</div>
                      <div className="flex-1 mx-2" data-id="element-2795">
                        <div className="h-2 rounded-full bg-muted overflow-hidden" data-id="element-2796">
                          <div className={`h-full ${index === 0 ? 'bg-blue-500' : index === 1 ? 'bg-green-500' : index === 2 ? 'bg-yellow-500' : 'bg-purple-500'}`} style={{
                        width: `${item.percentage}%`
                      }} data-id="element-2797"></div>
                        </div>
                      </div>
                      <div className="text-sm text-right w-24" data-id="element-2798">
                        {item.percentage}% ({formatCurrency(item.value)})
                      </div>
                    </div>)}
                </div>
              </CardContent>
            </Card>
            <Card data-id="element-2799">
              <CardHeader data-id="element-2800">
                <CardTitle data-id="element-2801">Your Allocation</CardTitle>
                <CardDescription data-id="element-2802">
                  What you'll receive based on your contribution
                </CardDescription>
              </CardHeader>
              <CardContent data-id="element-2803">
                <div className="space-y-4" data-id="element-2804">
                  <div className="p-4 border rounded-md bg-muted/30" data-id="element-2805">
                    <div className="flex justify-between mb-4" data-id="element-2806">
                      <div data-id="element-2807">
                        <p className="text-sm text-muted-foreground" data-id="element-2808">
                          Your Contribution
                        </p>
                        <p className="text-xl font-bold" data-id="element-2809">
                          {formatCurrency(25000)}
                        </p>
                      </div>
                      <div data-id="element-2810">
                        <p className="text-sm text-muted-foreground" data-id="element-2811">
                          Your Share
                        </p>
                        <p className="text-xl font-bold" data-id="element-2812">25%</p>
                      </div>
                    </div>
                    <div className="space-y-2" data-id="element-2813">
                      <p className="text-sm font-medium" data-id="element-2814">
                        Estimated Allocation:
                      </p>
                      <table className="w-full text-sm" data-id="element-2815">
                        <thead data-id="element-2816">
                          <tr className="border-b" data-id="element-2817">
                            <th className="text-left py-2" data-id="element-2818">Device Type</th>
                            <th className="text-right py-2" data-id="element-2819">Units</th>
                            <th className="text-right py-2" data-id="element-2820">Value</th>
                          </tr>
                        </thead>
                        <tbody data-id="element-2821">
                          {syndicateData.allocation.map((item, index) => <tr key={index} className="border-b" data-id="element-2822">
                              <td className="py-2" data-id="element-2823">{item.type}</td>
                              <td className="py-2 text-right" data-id="element-2824">
                                {(item.value * 0.25 / 750).toFixed(1)}
                              </td>
                              <td className="py-2 text-right" data-id="element-2825">
                                {formatCurrency(item.value * 0.25)}
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="flex justify-center" data-id="element-2826">
                    <Button onClick={() => navigate(`/syndicate-contribution/${id}`)} data-id="element-2827">
                      <DollarSignIcon className="mr-2 h-4 w-4" data-id="element-2828" />
                      Increase Contribution
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        <TabsContent value="activity" className="space-y-6" data-id="element-2829">
          <Card data-id="element-2830">
            <CardHeader data-id="element-2831">
              <CardTitle data-id="element-2832">Syndicate Timeline</CardTitle>
              <CardDescription data-id="element-2833">
                Chronological history of syndicate events
              </CardDescription>
            </CardHeader>
            <CardContent data-id="element-2834">
              <div className="relative pl-6 space-y-6" data-id="element-2835">
                <div className="absolute left-0 top-2 bottom-0 w-px bg-border" data-id="element-2836"></div>
                <div className="relative" data-id="element-2837">
                  <div className="absolute left-[-23px] top-1 h-4 w-4 rounded-full bg-primary" data-id="element-2838"></div>
                  <div className="space-y-1" data-id="element-2839">
                    <div className="flex items-center" data-id="element-2840">
                      <h3 className="text-base font-medium" data-id="element-2841">
                        Syndicate Created
                      </h3>
                      <Badge variant="outline" className="ml-2" data-id="element-2842">
                        Current Phase
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground" data-id="element-2843">
                      Alex Johnson created the syndicate on{' '}
                      {syndicateData.createdAt}
                    </p>
                    <div className="flex items-center text-xs text-muted-foreground mt-1" data-id="element-2844">
                      <ClockIcon className="h-3 w-3 mr-1" data-id="element-2845" />
                      <span data-id="element-2846">July 01, 2023 at 10:24 AM</span>
                    </div>
                  </div>
                </div>
                <div className="relative" data-id="element-2847">
                  <div className="absolute left-[-23px] top-1 h-4 w-4 rounded-full bg-muted-foreground/30" data-id="element-2848"></div>
                  <div className="space-y-1" data-id="element-2849">
                    <h3 className="text-base font-medium" data-id="element-2850">Funding Deadline</h3>
                    <p className="text-sm text-muted-foreground" data-id="element-2851">
                      Target date to reach funding goal
                    </p>
                    <div className="flex items-center text-xs text-muted-foreground mt-1" data-id="element-2852">
                      <ClockIcon className="h-3 w-3 mr-1" data-id="element-2853" />
                      <span data-id="element-2854">July 25, 2023 at 11:59 PM</span>
                    </div>
                  </div>
                </div>
                <div className="relative" data-id="element-2855">
                  <div className="absolute left-[-23px] top-1 h-4 w-4 rounded-full bg-muted-foreground/30" data-id="element-2856"></div>
                  <div className="space-y-1" data-id="element-2857">
                    <h3 className="text-base font-medium" data-id="element-2858">Auction Bidding</h3>
                    <p className="text-sm text-muted-foreground" data-id="element-2859">
                      Syndicate places bids in the target auction
                    </p>
                    <div className="flex items-center text-xs text-muted-foreground mt-1" data-id="element-2860">
                      <ClockIcon className="h-3 w-3 mr-1" data-id="element-2861" />
                      <span data-id="element-2862">Estimated: July 26-27, 2023</span>
                    </div>
                  </div>
                </div>
                <div className="relative" data-id="element-2863">
                  <div className="absolute left-[-23px] top-1 h-4 w-4 rounded-full bg-muted-foreground/30" data-id="element-2864"></div>
                  <div className="space-y-1" data-id="element-2865">
                    <h3 className="text-base font-medium" data-id="element-2866">
                      Inventory Distribution
                    </h3>
                    <p className="text-sm text-muted-foreground" data-id="element-2867">
                      Acquired devices are distributed to syndicate members
                    </p>
                    <div className="flex items-center text-xs text-muted-foreground mt-1" data-id="element-2868">
                      <ClockIcon className="h-3 w-3 mr-1" data-id="element-2869" />
                      <span data-id="element-2870">Estimated: August 1-5, 2023</span>
                    </div>
                  </div>
                </div>
                <div className="relative" data-id="element-2871">
                  <div className="absolute left-[-23px] top-1 h-4 w-4 rounded-full bg-muted-foreground/30" data-id="element-2872"></div>
                  <div className="space-y-1" data-id="element-2873">
                    <h3 className="text-base font-medium" data-id="element-2874">
                      Syndicate Completion
                    </h3>
                    <p className="text-sm text-muted-foreground" data-id="element-2875">
                      Final reports and syndicate closure
                    </p>
                    <div className="flex items-center text-xs text-muted-foreground mt-1" data-id="element-2876">
                      <ClockIcon className="h-3 w-3 mr-1" data-id="element-2877" />
                      <span data-id="element-2878">Estimated: August 10, 2023</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-id="element-2879">
            <CardHeader data-id="element-2880">
              <CardTitle data-id="element-2881">Recent Activity</CardTitle>
              <CardDescription data-id="element-2882">
                Latest actions and updates in the syndicate
              </CardDescription>
            </CardHeader>
            <CardContent data-id="element-2883">
              <div className="space-y-4" data-id="element-2884">
                <div className="flex items-start p-4 border rounded-md" data-id="element-2885">
                  <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center mr-3 mt-0.5" data-id="element-2886">
                    <DollarSignIcon className="h-5 w-5" data-id="element-2887" />
                  </div>
                  <div className="flex-1" data-id="element-2888">
                    <div className="flex items-center justify-between" data-id="element-2889">
                      <p className="font-medium" data-id="element-2890">
                        Maria Garcia increased contribution by $5,000
                      </p>
                      <Badge variant="outline" data-id="element-2891">Contribution</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1" data-id="element-2892">
                      New total contribution: $30,000 (30% of syndicate)
                    </p>
                    <div className="flex items-center text-xs text-muted-foreground mt-2" data-id="element-2893">
                      <ClockIcon className="h-3 w-3 mr-1" data-id="element-2894" />
                      <span data-id="element-2895">2 hours ago</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-start p-4 border rounded-md" data-id="element-2896">
                  <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center mr-3 mt-0.5" data-id="element-2897">
                    <UserPlusIcon className="h-5 w-5" data-id="element-2898" />
                  </div>
                  <div className="flex-1" data-id="element-2899">
                    <div className="flex items-center justify-between" data-id="element-2900">
                      <p className="font-medium" data-id="element-2901">
                        Robert Chen joined the syndicate
                      </p>
                      <Badge variant="outline" data-id="element-2902">Membership</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1" data-id="element-2903">
                      Initial contribution: $10,000 (10% of syndicate)
                    </p>
                    <div className="flex items-center text-xs text-muted-foreground mt-2" data-id="element-2904">
                      <ClockIcon className="h-3 w-3 mr-1" data-id="element-2905" />
                      <span data-id="element-2906">1 day ago</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-start p-4 border rounded-md" data-id="element-2907">
                  <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center mr-3 mt-0.5" data-id="element-2908">
                    <RefreshCwIcon className="h-5 w-5" data-id="element-2909" />
                  </div>
                  <div className="flex-1" data-id="element-2910">
                    <div className="flex items-center justify-between" data-id="element-2911">
                      <p className="font-medium" data-id="element-2912">
                        Alex Johnson updated target allocation
                      </p>
                      <Badge variant="outline" data-id="element-2913">Configuration</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1" data-id="element-2914">
                      Adjusted allocation percentages for iPhone 13 Pro and
                      iPhone 12 Pro Max models
                    </p>
                    <div className="flex items-center text-xs text-muted-foreground mt-2" data-id="element-2915">
                      <ClockIcon className="h-3 w-3 mr-1" data-id="element-2916" />
                      <span data-id="element-2917">2 days ago</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-start p-4 border rounded-md" data-id="element-2918">
                  <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center mr-3 mt-0.5" data-id="element-2919">
                    <GavelIcon className="h-5 w-5" data-id="element-2920" />
                  </div>
                  <div className="flex-1" data-id="element-2921">
                    <div className="flex items-center justify-between" data-id="element-2922">
                      <p className="font-medium" data-id="element-2923">
                        Target auction price increased
                      </p>
                      <Badge variant="outline" data-id="element-2924">Market Update</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1" data-id="element-2925">
                      Current bid on target auction increased from $9,800 to
                      $10,250
                    </p>
                    <div className="flex items-center text-xs text-muted-foreground mt-2" data-id="element-2926">
                      <ClockIcon className="h-3 w-3 mr-1" data-id="element-2927" />
                      <span data-id="element-2928">3 days ago</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
            <CardFooter className="border-t" data-id="element-2929">
              <Button variant="outline" size="sm" className="ml-auto" data-id="element-2930">
                View All Activity
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>
        <TabsContent value="documents" className="space-y-6" data-id="element-2931">
          <Card data-id="element-2932">
            <CardHeader data-id="element-2933">
              <CardTitle data-id="element-2934">Syndicate Documents</CardTitle>
              <CardDescription data-id="element-2935">Important files and agreements</CardDescription>
            </CardHeader>
            <CardContent data-id="element-2936">
              <div className="space-y-4" data-id="element-2937">
                <div className="flex items-center p-4 border rounded-md justify-between" data-id="element-2938">
                  <div className="flex items-center" data-id="element-2939">
                    <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center mr-3" data-id="element-2940">
                      <FileTextIcon className="h-5 w-5" data-id="element-2941" />
                    </div>
                    <div data-id="element-2942">
                      <p className="font-medium" data-id="element-2943">Syndicate Agreement</p>
                      <p className="text-xs text-muted-foreground" data-id="element-2944">
                        PDF • 245KB • Uploaded July 1, 2023
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" data-id="element-2945">
                    Download
                  </Button>
                </div>
                <div className="flex items-center p-4 border rounded-md justify-between" data-id="element-2946">
                  <div className="flex items-center" data-id="element-2947">
                    <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center mr-3" data-id="element-2948">
                      <FileTextIcon className="h-5 w-5" data-id="element-2949" />
                    </div>
                    <div data-id="element-2950">
                      <p className="font-medium" data-id="element-2951">Escrow Terms & Conditions</p>
                      <p className="text-xs text-muted-foreground" data-id="element-2952">
                        PDF • 187KB • Uploaded July 1, 2023
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" data-id="element-2953">
                    Download
                  </Button>
                </div>
                <div className="flex items-center p-4 border rounded-md justify-between" data-id="element-2954">
                  <div className="flex items-center" data-id="element-2955">
                    <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center mr-3" data-id="element-2956">
                      <FileTextIcon className="h-5 w-5" data-id="element-2957" />
                    </div>
                    <div data-id="element-2958">
                      <p className="font-medium" data-id="element-2959">Allocation Guidelines</p>
                      <p className="text-xs text-muted-foreground" data-id="element-2960">
                        PDF • 125KB • Uploaded July 1, 2023
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" data-id="element-2961">
                    Download
                  </Button>
                </div>
                <div className="flex items-center p-4 border rounded-md justify-between" data-id="element-2962">
                  <div className="flex items-center" data-id="element-2963">
                    <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center mr-3" data-id="element-2964">
                      <FileTextIcon className="h-5 w-5" data-id="element-2965" />
                    </div>
                    <div data-id="element-2966">
                      <p className="font-medium" data-id="element-2967">Target Auction Details</p>
                      <p className="text-xs text-muted-foreground" data-id="element-2968">
                        PDF • 320KB • Uploaded July 1, 2023
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" data-id="element-2969">
                    Download
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-id="element-2970">
            <CardHeader data-id="element-2971">
              <CardTitle data-id="element-2972">Signatures & Approvals</CardTitle>
              <CardDescription data-id="element-2973">
                Status of member signatures on syndicate documents
              </CardDescription>
            </CardHeader>
            <CardContent data-id="element-2974">
              <div className="overflow-x-auto" data-id="element-2975">
                <table className="w-full" data-id="element-2976">
                  <thead data-id="element-2977">
                    <tr className="border-b" data-id="element-2978">
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground" data-id="element-2979">
                        Member
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground" data-id="element-2980">
                        Syndicate Agreement
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground" data-id="element-2981">
                        Escrow Terms
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground" data-id="element-2982">
                        Signed On
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-2983">
                    {syndicateData.members.map(member => <tr key={member.id} className="border-b hover:bg-muted/50" data-id="element-2984">
                        <td className="py-3 px-4" data-id="element-2985">
                          <div className="flex items-center gap-2" data-id="element-2986">
                            <Avatar className="h-8 w-8" data-id="element-2987">
                              <AvatarImage src={member.avatar} data-id="element-2988" />
                              <AvatarFallback data-id="element-2989">
                                {member.name.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="font-medium" data-id="element-2990">{member.name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4" data-id="element-2991">
                          {member.status === 'active' ? <div className="flex items-center" data-id="element-2992">
                              <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" data-id="element-2993" />
                              <span data-id="element-2994">Signed</span>
                            </div> : <div className="flex items-center" data-id="element-2995">
                              <ClockIcon className="h-4 w-4 text-yellow-500 mr-1" data-id="element-2996" />
                              <span data-id="element-2997">Pending</span>
                            </div>}
                        </td>
                        <td className="py-3 px-4" data-id="element-2998">
                          {member.status === 'active' ? <div className="flex items-center" data-id="element-2999">
                              <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" data-id="element-3000" />
                              <span data-id="element-3001">Signed</span>
                            </div> : <div className="flex items-center" data-id="element-3002">
                              <ClockIcon className="h-4 w-4 text-yellow-500 mr-1" data-id="element-3003" />
                              <span data-id="element-3004">Pending</span>
                            </div>}
                        </td>
                        <td className="py-3 px-4" data-id="element-3005">
                          {member.status === 'active' ? member.joinDate : '-'}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="analytics" className="space-y-6" data-id="element-3006">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3007">
            <Card data-id="element-3008">
              <CardHeader data-id="element-3009">
                <CardTitle data-id="element-3010">Capital Growth</CardTitle>
                <CardDescription data-id="element-3011">
                  Total capital raised over time
                </CardDescription>
              </CardHeader>
              <CardContent data-id="element-3012">
                <RechartsLineChart data={contributionData} height={250} color="#3b82f6" gradient={true} yAxisFormatter={value => `$${value}`} tooltipFormatter={value => [`$${value}`, 'Capital Raised']} data-id="element-3013" />
              </CardContent>
            </Card>
            <Card data-id="element-3014">
              <CardHeader data-id="element-3015">
                <CardTitle data-id="element-3016">Member Distribution</CardTitle>
                <CardDescription data-id="element-3017">
                  Capital contribution by member
                </CardDescription>
              </CardHeader>
              <CardContent data-id="element-3018">
                <div className="flex justify-center" data-id="element-3019">
                  <RechartsPieChart data={memberDistributionData} height={250} donut={true} innerRadius={60} outerRadius={100} tooltipFormatter={(value, name) => [`${value}%`, name]} data-id="element-3020" />
                </div>
              </CardContent>
            </Card>
          </div>
          <Card data-id="element-3021">
            <CardHeader data-id="element-3022">
              <CardTitle data-id="element-3023">Allocation Distribution</CardTitle>
              <CardDescription data-id="element-3024">
                How capital is allocated across device types
              </CardDescription>
            </CardHeader>
            <CardContent data-id="element-3025">
              <div className="space-y-4" data-id="element-3026">
                {syndicateData.allocation.map((item, index) => <div key={index} className="flex items-center" data-id="element-3027">
                    <div className="w-32 text-sm truncate" data-id="element-3028">{item.type}</div>
                    <div className="flex-1 mx-2" data-id="element-3029">
                      <div className="h-2 rounded-full bg-muted overflow-hidden" data-id="element-3030">
                        <div className={`h-full ${index === 0 ? 'bg-blue-500' : index === 1 ? 'bg-green-500' : index === 2 ? 'bg-yellow-500' : 'bg-purple-500'}`} style={{
                      width: `${item.percentage}%`
                    }} data-id="element-3031"></div>
                      </div>
                    </div>
                    <div className="text-sm text-right w-24" data-id="element-3032">
                      {item.percentage}% ({formatCurrency(item.value)})
                    </div>
                  </div>)}
              </div>
            </CardContent>
          </Card>
          <Card data-id="element-3033">
            <CardHeader data-id="element-3034">
              <CardTitle data-id="element-3035">Syndicate Performance Metrics</CardTitle>
              <CardDescription data-id="element-3036">Key performance indicators</CardDescription>
            </CardHeader>
            <CardContent data-id="element-3037">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-3038">
                <div className="p-4 border rounded-md bg-muted/30" data-id="element-3039">
                  <div className="flex items-center justify-between mb-2" data-id="element-3040">
                    <h3 className="text-sm font-medium" data-id="element-3041">Funding Progress</h3>
                    <Badge variant="outline" data-id="element-3042">90%</Badge>
                  </div>
                  <Progress value={90} className="h-2 mb-2" data-id="element-3043" />
                  <p className="text-xs text-muted-foreground" data-id="element-3044">
                    {formatCurrency(syndicateData.totalCapital)} of{' '}
                    {formatCurrency(syndicateData.targetCapital)} target
                  </p>
                </div>
                <div className="p-4 border rounded-md bg-muted/30" data-id="element-3045">
                  <div className="flex items-center justify-between mb-2" data-id="element-3046">
                    <h3 className="text-sm font-medium" data-id="element-3047">Time Remaining</h3>
                    <Badge variant="outline" data-id="element-3048">48%</Badge>
                  </div>
                  <Progress value={48} className="h-2 mb-2" data-id="element-3049" />
                  <p className="text-xs text-muted-foreground" data-id="element-3050">
                    12 days left of 25 day funding period
                  </p>
                </div>
                <div className="p-4 border rounded-md bg-muted/30" data-id="element-3051">
                  <div className="flex items-center justify-between mb-2" data-id="element-3052">
                    <h3 className="text-sm font-medium" data-id="element-3053">Avg. Contribution</h3>
                    <span className="font-medium" data-id="element-3054">{formatCurrency(18000)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground" data-id="element-3055">
                    Range: {formatCurrency(10000)} - {formatCurrency(30000)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      <div className="flex justify-between" data-id="element-3056">
        <Button variant="outline" onClick={() => navigate('/syndicate-hub')} data-id="element-3057">
          <ArrowLeftIcon className="mr-2 h-4 w-4" data-id="element-3058" />
          Back to Syndicate Hub
        </Button>
        <div className="flex space-x-2" data-id="element-3059">
          <Button variant="outline" onClick={() => navigate(`/syndicate-contribution/${id}`)} data-id="element-3060">
            <DollarSignIcon className="mr-2 h-4 w-4" data-id="element-3061" />
            Contribute
          </Button>
          <Button onClick={() => navigate(`/syndicate-distribution/${id}`)} data-id="element-3062">
            <BoxIcon className="mr-2 h-4 w-4" data-id="element-3063" />
            View Distribution
          </Button>
        </div>
      </div>
    </div>;
};
export default SyndicateDetail;